import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '~/shared/utils/translation';
import { usePage } from '~/templates/pages/hooks/usePage';
import { breakpointsRaw } from '~/theme/breakpoints';
import {
    StyledSearchStarsBox,
    StyledSearchStarsHint,
    StyledSearchWithStarsWrapper,
} from '../styled';
import { useFrame } from '~/shared/utils';

const CHAT_HINT_DISMISSED_KEY = 'chat_hint_dismissed';
const DEFAULT_CHAT_HINT_TEXT = 'Lad mig hjælpe dig med at finde det du søger 😊';

export type SearchWithChatLauncherProps = {
    children: React.ReactNode;
    /** When false, the chat hint is never shown (e.g. PLP sticky bar). Defaults to true. */
    showHint?: boolean;
};

/**
 * Wraps search input(s) with the AI chat launcher (stars box) and optional PDP hint.
 * Encapsulates all chat-hint and chat-visibility logic so N20MainMenu stays minimal.
 */
export const SearchWithChatLauncher: React.FC<SearchWithChatLauncherProps> = ({
    children,
    showHint = true,
}) => {
    const { translate } = useTranslation();
    const page = usePage();
    const isProductDetailsPage = page.type === 'p50ProductDetailsPage';
    const { data: frame } = useFrame();
    const [isChatHintVisible, setIsChatHintVisible] = useState(false);

    const dismissChatHint = useCallback(() => {
        setIsChatHintVisible(false);
        if (typeof window !== 'undefined') {
            window.sessionStorage.setItem(CHAT_HINT_DISMISSED_KEY, '1');
        }
    }, []);

    // Show hint on PDP only, with 10s auto-dismiss
    useEffect(() => {
        if (!showHint || !isProductDetailsPage) {
            setIsChatHintVisible(false);
            return;
        }
        if (typeof window === 'undefined') return;
        const isDismissed = window.sessionStorage.getItem(CHAT_HINT_DISMISSED_KEY) === '1';
        if (isDismissed) {
            setIsChatHintVisible(false);
            return;
        }
        setIsChatHintVisible(true);
        const timeoutId = window.setTimeout(dismissChatHint, 10000);
        return () => window.clearTimeout(timeoutId);
    }, [dismissChatHint, isProductDetailsPage, showHint]);

    // Hide native chat widget on PDP (header shows in-page launcher instead)
    useEffect(() => {
        const applyChatVisibility = () => {
            const chatContainer = document.getElementById('chat-container');
            if (!chatContainer) return;
            const isDesktopViewport = window.innerWidth >= breakpointsRaw.md;
            const shouldHideChat = isProductDetailsPage && !isDesktopViewport;
            chatContainer.style.display = shouldHideChat ? 'none' : '';
        };

        applyChatVisibility();
        window.addEventListener('resize', applyChatVisibility);
        const observer = new MutationObserver(applyChatVisibility);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class'],
        });
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', applyChatVisibility);
            const chatContainer = document.getElementById('chat-container');
            if (chatContainer) chatContainer.style.display = '';
        };
    }, [isProductDetailsPage]);

    const triggerChatOpen = useCallback(() => {
        dismissChatHint();
        const nativeChatButton = document.getElementById('chat-button') as HTMLButtonElement | null;
        if (nativeChatButton) {
            nativeChatButton.click();
            return;
        }
        const nativeChatContainer = document.getElementById('chat-container') as HTMLElement | null;
        if (nativeChatContainer) {
            nativeChatContainer.click();
            return;
        }
        window.dispatchEvent(
            new CustomEvent('chat_open', { detail: { chat_type: 'shopping_assistant' } }),
        );
    }, [dismissChatHint]);

    const chatHintText = translate('header.chatHint') || DEFAULT_CHAT_HINT_TEXT;

    return (
        <StyledSearchWithStarsWrapper>
            {showHint && isChatHintVisible && frame.settings?.enableAIChatbot && (
                <StyledSearchStarsHint onClick={dismissChatHint}>
                    {chatHintText}
                </StyledSearchStarsHint>
            )}

            {frame.settings?.enableAIChatbot && (
                <StyledSearchStarsBox
                    type="button"
                    aria-label="Open chat"
                    onClick={triggerChatOpen}
                >
                    {translate('header.chatLabel')}
                </StyledSearchStarsBox>
            )}

            {children}
        </StyledSearchWithStarsWrapper>
    );
};
